import { useState } from "react";

const SYSTEM_PROMPT = `You are a surf data assistant for the Barrelcast app. The user will paste raw WSL rankings text copied from worldsurfleague.com.

Your job is to extract the top 10 surfers and return ONLY a valid JSON object — no markdown, no explanation, no code fences.

The JSON must follow this exact shape:
{
  "meta": {
    "afterEvent": 5,
    "lastEventName": "Surf City El Salvador Pro",
    "lastEventLocation": "Punta Roca, La Libertad, El Salvador",
    "lastUpdated": "2026-06-15",
    "nextEventName": "VIVO Rio Pro",
    "nextEventWindow": "Jun 19 – 27, 2026",
    "nextEventLocation": "Itaúna Beach, Saquarema, Brazil"
  },
  "men": [
    {
      "rank": 1,
      "name": "Ítalo Ferreira",
      "country": "Brazil",
      "flag": "🇧🇷",
      "points": 27500,
      "events": 5,
      "bestResult": "1st – New Zealand",
      "results": [
        { "event": "Bells Beach", "place": "—" },
        { "event": "Margaret River", "place": "—" },
        { "event": "Gold Coast", "place": 9 },
        { "event": "New Zealand", "place": 1 },
        { "event": "El Salvador", "place": 3 },
        { "event": "Rio Pro", "place": "—" },
        { "event": "Tahiti", "place": "—" },
        { "event": "Fiji", "place": "—" },
        { "event": "Trestles", "place": "—" },
        { "event": "Abu Dhabi", "place": "—" },
        { "event": "Portugal", "place": "—" },
        { "event": "Pipe Masters", "place": "—" }
      ]
    }
  ],
  "women": [ /* same shape */ ]
}

Rules:
- Extract exactly top 10 men and top 10 women
- Use correct country flag emojis (Hawaii surfers use 🇺🇸)
- "place" values must be numbers (1, 2, 3, 5, 9, 13, 17, 25) or the string "—" for events not yet run or DNS
- "bestResult" format: "1st – EventName" using the short event name
- "lastUpdated" must be today's date in YYYY-MM-DD format
- Fill in meta fields as best you can from the pasted text — if next event is unknown, use your knowledge of the 2026 WSL CT schedule
- Return ONLY the raw JSON object. No markdown. No explanation.`;

const CODE_TO_FLAG = {
  "US": "🇺🇸", "AU": "🇦🇺", "BR": "🇧🇷", "ZA": "🇿🇦",
  "FR": "🇫🇷", "JP": "🇯🇵", "IT": "🇮🇹", "PT": "🇵🇹",
  "ES": "🇪🇸", "ID": "🇮🇩", "NZ": "🇳🇿", "CR": "🇨🇷",
  "MA": "🇲🇦", "PF": "🇵🇫", "FJ": "🇫🇯",
};

const FLAG_MAP = {
  "United States": "🇺🇸", "USA": "🇺🇸", "Hawaii": "🇺🇸",
  "Australia": "🇦🇺", "Brazil": "🇧🇷", "South Africa": "🇿🇦",
  "France": "🇫🇷", "Japan": "🇯🇵", "Italy": "🇮🇹",
  "Portugal": "🇵🇹", "Spain": "🇪🇸", "Indonesia": "🇮🇩",
  "New Zealand": "🇳🇿", "Costa Rica": "🇨🇷", "Morocco": "🇲🇦",
  "Tahiti": "🇵🇫", "French Polynesia": "🇵🇫", "Fiji": "🇫🇯",
};

export default function RankingsUpdater() {
  const [pasteMen, setPasteMen] = useState("");
  const [pasteWomen, setPasteWomen] = useState("");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const [copied, setCopied] = useState(false);
  const [step, setStep] = useState("paste"); // paste | preview | done

  const callAPI = async (text, tour) => {
    const response = await fetch("/api/extract-rankings", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ text, tour }),
    });
    const data = await response.json();
    if (!response.ok) throw new Error(data.error ?? "API error");
    return data;
  };

  const process = async () => {
    if (!pasteMen.trim() && !pasteWomen.trim()) return;
    setLoading(true);
    setError(null);
    setResult(null);

    try {
      const [menData, womenData] = await Promise.all([
        pasteMen.trim() ? callAPI(pasteMen, "men") : Promise.resolve({ men: [], meta: {} }),
        pasteWomen.trim() ? callAPI(pasteWomen, "women") : Promise.resolve({ women: [] }),
      ]);

      setResult({
        meta: menData.meta || womenData.meta,
        men: menData.men || [],
        women: womenData.women || [],
      });
      setStep("preview");
    } catch (err) {
      setError(err.message ?? "Couldn't parse the rankings. Make sure you pasted the full table from worldsurfleague.com and try again.");
    } finally {
      setLoading(false);
    }
  };

  const generateTs = (data) => {
    const { meta, men, women } = data;

    const events = [
      "Bells Beach","Margaret River","Gold Coast","New Zealand",
      "El Salvador","Rio Pro","Tahiti","Fiji","Trestles",
      "Abu Dhabi","Portugal","Pipe Masters"
    ];

    const surferTs = (s) => {
      const results = events.map(ev => {
        const r = s.results?.find(x => x.event === ev);
        const p = r?.place;
        return p === undefined || p === "—" ? `"—"` : p;
      });
      return `  { rank: ${s.rank}, name: "${s.name}", country: "${s.country}", flag: "${CODE_TO_FLAG[s.flag] || s.flag || FLAG_MAP[s.country] || "🏄"}", points: ${s.points}, events: ${s.events}, bestResult: "${s.bestResult}", results: m([${results.join(", ")}]) },`;
    };

    return `export type CompetitionStatus = "upcoming" | "live" | "completed";

export type Surfer = {
  rank: number;
  name: string;
  country: string;
  flag: string;
  points: number;
  events: number;
  bestResult: string;
  results: { event: string; place: number | "—" }[];
};

export const tourEvents2026 = [
  "Bells Beach", "Margaret River", "Gold Coast", "New Zealand",
  "El Salvador", "Rio Pro", "Tahiti", "Fiji", "Trestles",
  "Abu Dhabi", "Portugal", "Pipe Masters",
] as const;

const m = (results: (number | "—")[]): Surfer["results"] =>
  tourEvents2026.map((event, i) => ({ event, place: results[i] ?? "—" }));

// ─── REAL STANDINGS after Event ${meta.afterEvent} — ${meta.lastEventName} ───
// Source: worldsurfleague.com
// Last verified: ${meta.lastUpdated}
// Next event: ${meta.nextEventName}, ${meta.nextEventWindow}

export const menRankings: Surfer[] = [
${men.map(surferTs).join("\n")}
];

export const womenRankings: Surfer[] = [
${women.map(surferTs).join("\n")}
];

export const rankingsMeta = {
  lastUpdated: "${meta.lastUpdated}",
  afterEvent: ${meta.afterEvent},
  lastEventName: "${meta.lastEventName}",
  lastEventLocation: "${meta.lastEventLocation}",
  nextEventName: "${meta.nextEventName}",
  nextEventWindow: "${meta.nextEventWindow}",
  nextEventLocation: "${meta.nextEventLocation}",
};`;
  };

  const tsCode = result ? generateTs(result) : "";

  const copy = async () => {
    await navigator.clipboard.writeText(tsCode);
    setCopied(true);
    setTimeout(() => setCopied(false), 2500);
  };

  const reset = () => {
    setPasteMen("");
    setPasteWomen("");
    setResult(null);
    setError(null);
    setStep("paste");
    setCopied(false);
  };

  // ── styles ──────────────────────────────────────────────────────────────────
  const navy   = "#0d2137";
  const panel  = "#112844";
  const border = "#1e3a55";
  const orange = "#e8500a";
  const chalk  = "#f0e9db";
  const fog    = "#7fa8be";
  const green  = "#22c55e";

  return (
    <div style={{ background: navy, minHeight: "100vh", fontFamily: "system-ui, sans-serif", color: chalk, padding: "24px 16px" }}>

      {/* Header */}
      <div style={{ maxWidth: 640, margin: "0 auto 28px" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 6 }}>
          <div style={{ background: orange, borderRadius: 10, width: 36, height: 36, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 18 }}>🏆</div>
          <div>
            <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: "0.2em", textTransform: "uppercase", color: orange }}>Barrelcast Admin</div>
            <div style={{ fontSize: 22, fontWeight: 800, letterSpacing: "-0.02em" }}>Rankings Updater</div>
          </div>
        </div>
        <p style={{ fontSize: 13, color: fog, margin: 0, lineHeight: 1.5 }}>
          After each WSL event, paste the rankings table here. Claude extracts the data and generates a ready-to-commit <code style={{ background: border, padding: "1px 5px", borderRadius: 4, fontSize: 12 }}>rankings.ts</code> file in seconds.
        </p>
      </div>

      <div style={{ maxWidth: 640, margin: "0 auto" }}>

        {/* Step indicator */}
        <div style={{ display: "flex", gap: 8, marginBottom: 20 }}>
          {["Paste", "Preview", "Commit"].map((s, i) => {
            const active = (step === "paste" && i === 0) || (step === "preview" && i === 1) || (step === "done" && i === 2);
            const done = (step === "preview" && i === 0) || (step === "done" && i <= 1);
            return (
              <div key={s} style={{ display: "flex", alignItems: "center", gap: 6 }}>
                <div style={{
                  width: 22, height: 22, borderRadius: "50%", fontSize: 11, fontWeight: 700,
                  display: "flex", alignItems: "center", justifyContent: "center",
                  background: done ? green : active ? orange : border,
                  color: (done || active) ? "#fff" : fog,
                }}>
                  {done ? "✓" : i + 1}
                </div>
                <span style={{ fontSize: 12, fontWeight: 600, color: active ? chalk : done ? green : fog }}>{s}</span>
                {i < 2 && <div style={{ width: 24, height: 1, background: border, marginLeft: 2 }} />}
              </div>
            );
          })}
        </div>

        {/* STEP 1 — Paste */}
        {step === "paste" && (
          <div style={{ background: panel, border: `1px solid ${border}`, borderRadius: 14, padding: 20 }}>
            <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: "0.15em", textTransform: "uppercase", color: orange, marginBottom: 10 }}>
              How to get the data
            </div>
            <ol style={{ fontSize: 13, color: fog, lineHeight: 1.8, margin: "0 0 16px", paddingLeft: 18 }}>
              <li>Go to <a href="https://www.worldsurfleague.com/athletes/tour/mct" target="_blank" rel="noopener noreferrer" style={{ color: orange }}>worldsurfleague.com/athletes/tour/mct</a></li>
              <li>Select all the rankings text (Ctrl+A or Cmd+A on the table)</li>
              <li>Copy and paste it below</li>
              <li>Do the same for women at <a href="https://www.worldsurfleague.com/athletes/tour/wct" target="_blank" rel="noopener noreferrer" style={{ color: orange }}>…/wct</a></li>
            </ol>

            <div style={{ fontSize: 11, fontWeight: 700, color: orange, textTransform: "uppercase", letterSpacing: "0.1em", marginBottom: 4 }}>Men's Rankings</div>
            <textarea
              value={pasteMen}
              onChange={e => setPasteMen(e.target.value)}
              placeholder="Paste men's rankings from worldsurfleague.com/athletes/tour/mct..."
              style={{
                width: "100%", minHeight: 130, background: "#0a1c2e", border: `1px solid ${border}`,
                borderRadius: 8, padding: "12px", color: chalk, fontSize: 13, lineHeight: 1.5,
                resize: "vertical", outline: "none", boxSizing: "border-box",
                fontFamily: "system-ui, sans-serif", marginBottom: 12,
              }}
            />
            <div style={{ fontSize: 11, fontWeight: 700, color: orange, textTransform: "uppercase", letterSpacing: "0.1em", marginBottom: 4 }}>Women's Rankings</div>
            <textarea
              value={pasteWomen}
              onChange={e => setPasteWomen(e.target.value)}
              placeholder="Paste women's rankings from worldsurfleague.com/athletes/tour/wct..."
              style={{
                width: "100%", minHeight: 130, background: "#0a1c2e", border: `1px solid ${border}`,
                borderRadius: 8, padding: "12px", color: chalk, fontSize: 13, lineHeight: 1.5,
                resize: "vertical", outline: "none", boxSizing: "border-box",
                fontFamily: "system-ui, sans-serif",
              }}
            />

            {error && (
              <div style={{ marginTop: 10, padding: "10px 14px", background: "rgba(239,68,68,0.12)", border: "1px solid rgba(239,68,68,0.3)", borderRadius: 8, fontSize: 13, color: "#f87171" }}>
                {error}
              </div>
            )}

            <button
              onClick={process}
              disabled={loading || (!pasteMen.trim() && !pasteWomen.trim())}
              style={{
                marginTop: 14, width: "100%", padding: "12px", borderRadius: 8,
                background: loading || (!pasteMen.trim() && !pasteWomen.trim()) ? border : orange,
                color: chalk, fontWeight: 700, fontSize: 14, border: "none",
                cursor: loading || (!pasteMen.trim() && !pasteWomen.trim()) ? "not-allowed" : "pointer",
                letterSpacing: "0.05em", textTransform: "uppercase",
                transition: "background 0.2s",
              }}
            >
              {loading ? "⏳  Extracting rankings…" : "⚡  Extract Rankings"}
            </button>
          </div>
        )}

        {/* STEP 2 — Preview */}
        {step === "preview" && result && (
          <div>
            {/* Meta card */}
            <div style={{ background: panel, border: `1px solid ${border}`, borderRadius: 14, padding: 16, marginBottom: 14 }}>
              <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: "0.15em", textTransform: "uppercase", color: orange, marginBottom: 10 }}>Event info extracted</div>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "6px 16px" }}>
                {[
                  ["After event", result.meta.afterEvent],
                  ["Last event", result.meta.lastEventName],
                  ["Updated", result.meta.lastUpdated],
                  ["Next event", result.meta.nextEventName],
                ].map(([label, val]) => (
                  <div key={label}>
                    <div style={{ fontSize: 10, color: fog, textTransform: "uppercase", letterSpacing: "0.1em" }}>{label}</div>
                    <div style={{ fontSize: 13, fontWeight: 600, color: chalk }}>{val}</div>
                  </div>
                ))}
              </div>
            </div>

            {/* Top 3 preview */}
            {["men", "women"].map(tour => (
              <div key={tour} style={{ background: panel, border: `1px solid ${border}`, borderRadius: 14, padding: 16, marginBottom: 14 }}>
                <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: "0.15em", textTransform: "uppercase", color: orange, marginBottom: 10 }}>
                  {tour === "men" ? "Men's" : "Women's"} top 10
                </div>
                {result[tour].slice(0, 10).map(s => (
                  <div key={s.rank} style={{ display: "flex", alignItems: "center", gap: 10, padding: "8px 0", borderBottom: `1px solid ${border}` }}>
                    <div style={{
                      width: 28, height: 28, borderRadius: 6, display: "flex", alignItems: "center", justifyContent: "center",
                      fontWeight: 800, fontSize: 13,
                      background: s.rank === 1 ? orange : s.rank <= 3 ? "rgba(232,80,10,0.2)" : border,
                      color: s.rank === 1 ? "#fff" : chalk,
                    }}>{s.rank}</div>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontSize: 14, fontWeight: 700 }}>{s.flag} {s.name}</div>
                      <div style={{ fontSize: 11, color: fog }}>{s.country} · {s.bestResult}</div>
                    </div>
                    <div style={{ fontWeight: 800, fontSize: 15 }}>{s.points.toLocaleString()}<span style={{ fontSize: 10, color: fog, marginLeft: 3 }}>pts</span></div>
                  </div>
                ))}
              </div>
            ))}

            {/* Generated code */}
            <div style={{ background: "#0a1c2e", border: `1px solid ${border}`, borderRadius: 14, padding: 16, marginBottom: 14 }}>
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 10 }}>
                <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: "0.15em", textTransform: "uppercase", color: orange }}>
                  Generated rankings.ts
                </div>
                <button
                  onClick={copy}
                  style={{
                    padding: "5px 14px", borderRadius: 6, border: `1px solid ${copied ? green : border}`,
                    background: copied ? "rgba(34,197,94,0.1)" : "transparent",
                    color: copied ? green : fog, fontSize: 12, fontWeight: 700,
                    cursor: "pointer", transition: "all 0.2s",
                  }}
                >
                  {copied ? "✓ Copied!" : "Copy"}
                </button>
              </div>
              <pre style={{
                margin: 0, fontSize: 11, color: fog, lineHeight: 1.6,
                maxHeight: 260, overflowY: "auto", whiteSpace: "pre-wrap", wordBreak: "break-all",
              }}>
                {tsCode.slice(0, 800)}…
              </pre>
            </div>

            {/* Commit instructions */}
            <div style={{ background: panel, border: `1px solid ${border}`, borderRadius: 14, padding: 16, marginBottom: 14 }}>
              <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: "0.15em", textTransform: "uppercase", color: green, marginBottom: 10 }}>
                3 steps to go live
              </div>
              {[
                "Click Copy above to copy the full rankings.ts file",
                "Open data/rankings.ts in your GitHub repo",
                "Select all, paste, commit - app updates instantly 🤙",
              ].map((step, i) => (
                <div key={i} style={{ display: "flex", gap: 10, marginBottom: 8 }}>
                  <div style={{ width: 20, height: 20, borderRadius: "50%", background: orange, color: "#fff", fontSize: 11, fontWeight: 700, display: "flex", alignItems: "center", justifyContent: "center", shrink: 0, flexShrink: 0 }}>{i + 1}</div>
                  <div style={{ fontSize: 13, color: fog, lineHeight: 1.5 }}>{step}</div>
                </div>
              ))}
            </div>

            <div style={{ display: "flex", gap: 10 }}>
              <button onClick={reset} style={{ flex: 1, padding: "11px", borderRadius: 8, background: "transparent", border: `1px solid ${border}`, color: fog, fontWeight: 700, fontSize: 13, cursor: "pointer" }}>
                ← Start over
              </button>
              <button onClick={copy} style={{ flex: 2, padding: "11px", borderRadius: 8, background: copied ? green : orange, border: "none", color: "#fff", fontWeight: 700, fontSize: 13, cursor: "pointer", transition: "background 0.2s" }}>
                {copied ? "✓ Copied to clipboard!" : "📋  Copy rankings.ts"}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
